import type { InvestorField, ListColumn } from "../types";

export const SELECTED_KEY = "danfrick-crm-selected-v1";
export const COLUMNS_KEY = "danfrick-crm-columns-v1";
export const DISCOVERY_LIMIT = 10;
export const DISCOVERY_MIN_SCORE = 6;
export const DISCOVERY_MAX_ATTEMPTS = 3;
export const SPREADSHEET_BATCH_SIZE = 8;

export const DISCOVERY_DEFAULT_BRIEF = [
  "Find all plausible capital for RxexR with an emphasis on pre-seed health tech.",
  "Prioritize healthcare technology, provider workflow, vertical SaaS, AI workflow, marketplace, commerce infrastructure, and relevant operator angels.",
  "Use an 80/20 search mix: mostly investors similar to our strongest CRM signals, with a small amount of adjacent exploration.",
  "Prefer US investors, but include exceptional non-US fits.",
].join("\n");

export const statusOrder = [
  "Not Started",
  "Researching",
  "Need Intro",
  "Intro Requested",
  "Intro Sent",
  "Contacted",
  "Meeting Scheduled",
  "In Talks",
  "Diligence",
  "Applied",
  "VC Passed",
  "Not Pursuing",
  "Filtered Out",
  "Committed",
  "Closed",
];

export const inactiveStatuses = new Set(["Not Started", "VC Passed", "Not Pursuing", "Filtered Out"]);
export const hiddenStatuses = new Set(["Filtered Out"]);
export const priorityOrder = ["High", "Medium", "Low"];
export const priorityScore: Record<string, number> = { High: 3, Medium: 2, Low: 1 };

export const fieldDefinitions: Array<{ key: InvestorField; label: string }> = [
  { key: "firm", label: "Firm" },
  { key: "contact", label: "Investor / Contact" },
  { key: "type", label: "Type" },
  { key: "status", label: "Status" },
  { key: "priority", label: "Priority" },
  { key: "round", label: "Round" },
  { key: "checkSize", label: "Check Size" },
  { key: "introSource", label: "Intro / Source" },
  { key: "linkNotes", label: "Link / Notes" },
  { key: "dateContacted", label: "Date Contacted" },
  { key: "followUpDate", label: "Follow-up Date" },
  { key: "nextStep", label: "Next Step" },
  { key: "notes", label: "Notes" },
  { key: "aiFitScore", label: "AI Fit Score" },
  { key: "people", label: "People" },
  { key: "aiResearch", label: "AI Research" },
  { key: "aiResearchSources", label: "AI Research Sources" },
  { key: "aiResearchedAt", label: "AI Researched At" },
  { key: "discoveryKey", label: "Discovery Key" },
  { key: "discoveryFoundAt", label: "Discovery Found At" },
  { key: "discoveryQuery", label: "Discovery Query" },
  { key: "discoveryReason", label: "Discovery Reason" },
];

export const dbFields = {
  firm: "firm",
  contact: "contact",
  type: "investor_type",
  linkNotes: "link_notes",
  status: "status",
  introSource: "intro_source",
  dateContacted: "date_contacted",
  nextStep: "next_step",
  followUpDate: "follow_up_date",
  notes: "notes",
  checkSize: "check_size",
  round: "funding_round",
  priority: "priority",
  aiFitScore: "ai_fit_score",
  people: "people",
  aiResearch: "ai_research",
  aiResearchSources: "ai_research_sources",
  aiResearchedAt: "ai_researched_at",
  discoveryKey: "discovery_key",
  discoveryFoundAt: "discovery_found_at",
  discoveryQuery: "discovery_query",
  discoveryReason: "discovery_reason",
} as const;

export const nullableFields = new Set<InvestorField>([
  "dateContacted",
  "followUpDate",
  "aiResearchedAt",
  "discoveryFoundAt",
]);
export const integerFields = new Set<InvestorField>(["aiFitScore"]);
export const jsonFields = new Set<InvestorField>(["people"]);
export const personFields = ["name", "title", "linkedin", "xProfile", "email", "notes"] as const;

export const listColumns: ListColumn[] = [
  { key: "name", label: "Name", width: "minmax(180px, 1.2fr)", minWidth: 180, required: true },
  { key: "status", label: "Status", width: "minmax(112px, 0.55fr)", minWidth: 112 },
  { key: "activity", label: "Activity", width: "minmax(142px, 0.75fr)", minWidth: 142 },
  { key: "nextStep", label: "Next Step", width: "minmax(160px, 1fr)", minWidth: 160 },
  { key: "type", label: "Type", width: "minmax(112px, 0.55fr)", minWidth: 112 },
  { key: "priority", label: "Priority", width: "minmax(100px, 0.45fr)", minWidth: 100 },
  { key: "checkSize", label: "Check Size", width: "minmax(118px, 0.6fr)", minWidth: 118 },
  { key: "round", label: "Round", width: "minmax(112px, 0.55fr)", minWidth: 112 },
  { key: "fitScore", label: "Research Rank", width: "minmax(122px, 0.6fr)", minWidth: 122 },
  { key: "people", label: "People", width: "minmax(108px, 0.5fr)", minWidth: 108 },
  { key: "followUp", label: "Follow-up", width: "minmax(122px, 0.6fr)", minWidth: 122 },
  { key: "source", label: "Source", width: "minmax(140px, 0.75fr)", minWidth: 140 },
  { key: "research", label: "Research", width: "minmax(128px, 0.65fr)", minWidth: 128 },
];

export const defaultListColumnKeys = ["name", "status", "activity", "nextStep"];
